import { timeSlots } from "./constants"

const minLegionSize = 20
const maxLegionSize = 30
const maxLegions = 5

class Legion {
  constructor(timeSlot, wizard) {
    this.timeSlot = timeSlot
    this.characters = []
    this.wizard = wizard
  }

  get open () {
    return this.characters.length < maxLegionSize
  }

  get incomplete () {
    return this.characters.length < minLegionSize
  }

  assign (assignee) {
    let characters = Array.isArray(assignee) ? assignee : [assignee]
    characters.forEach((character) => {
      this.characters.push(character)
      this.wizard.unassignedCharacters = this.wizard.unassignedCharacters.filter((unassignedCharacter) => unassignedCharacter !== character)
    })
  }

  moveToLegion (moving, destinationLegion) {
    let movingCharacters = Array.isArray(moving) ? moving : [moving]
    this.characters = this.characters.filter((character) => !movingCharacters.includes(character))
    destinationLegion.assign(movingCharacters)
  }
}

export class Wizard {
  constructor(characters, options = {}) {
    this.options = { 
      lowPopularityThreshold: options.lowPopularityThreshold || 10, // When a time slot has less than this amount of participants, participants will tend to be distributed to other legions rather than having a separate legion for that time slot
      disabledTimeSlots: options.disabledTimeSlots || [],
      algorithmPriority: options.algorithmPriority || 'lowerAvailability', // 'lowerAvailability' | 'higherAvailability'
      groupStrategy: options.groupStrategy || 'maximizeParticipation', // 'maximizeParticipation' | 'minimizeIncompatibilities'
      fillStrategy: options.fillStrategy || 'noAvailability', // 'noAvailability' | 'unmatchedAvailability'
    }
    this.timeSlots = timeSlots.filter((timeSlot) => !this.options.disabledTimeSlots.includes(timeSlot))
    this.unassignedCharacters = characters.sort((character1, character2) => this.options.algorithmPriority === 'lowerAvailability' ? character1.availabilities.length - character2.availabilities.length : character2.availabilities.length - character1.availabilities.length)
    this.legions = []
  }

  get availableCharacters () {
    return this.unassignedCharacters.filter((character) => character.availabilities.length)
  }
  
  get unavailableCharacters () {
    return this.unassignedCharacters.filter((character) => !character.availabilities.length)
  }

  get sortedAvailabilities () {
    return this.timeSlots.map((timeSlot) => {
      return { timeSlot, availableCharacters: this.unassignedCharacters.filter((character) => character.availabilities.includes(timeSlot)) }
    }).sort((timeSlot1, timeSlot2) => timeSlot2.availableCharacters.length - timeSlot1.availableCharacters.length)
  }

  get incompleteLegions () {
    return this.legions.filter((legion) => legion.incomplete)
  }

  fillLegion (legion) {
    let missingSpots = minLegionSize - legion.characters.length
    legion.assign(this.unassignedCharacters.slice(0, missingSpots))
  }

  createLegion (timeSlot) {
    let newLegion = new Legion(timeSlot, this)
    this.legions.push(newLegion)

    return newLegion
  }

  disbandLegion (disbandingLegion) {
    this.legions = this.legions.filter((legion) => legion !== disbandingLegion)
  }

  distribute (characters) {
    characters.forEach((character) => {
      let legionForCharacter = this.legions.filter((legion) => legion.open && character.availabilities.includes(legion.timeSlot)).sort((legion1, legion2) => legion1.characters.length - legion2.characters.length)[0]
      if (legionForCharacter) legionForCharacter.assign(character)
      else if (!this.unassignedCharacters.includes(character)) this.unassignedCharacters.push(character)
    })
  }

  createLegions () {
    // First draft legions
    this.availableCharacters.forEach(() => {
      let timeSlot = this.sortedAvailabilities[0].timeSlot
      let presentLegion = this.legions.filter((legion) => {
        const forTimeSlot = legion.timeSlot === timeSlot

        return legion.open && forTimeSlot
      }).sort((legion1, legion2) => legion1.availability - legion2.availability)[0]
      let currentLegion = presentLegion || (this.legions.length < maxLegions && this.createLegion(timeSlot))

      if (currentLegion) {
        let charactersForTimeSlot = this.availableCharacters.filter((character) => character.availabilities.includes(timeSlot))
        let characterWithOnlyThatAvailability = charactersForTimeSlot.find((character) => {
          return character.availabilities.length === 1 && character.availabilities[0] === timeSlot
        })
        let characterWithUnpopularOtherAvailabilities = charactersForTimeSlot.find((character) => {
          let otherAvailabilities = character.availabilities.filter((availability) => availability !== timeSlot)
          otherAvailabilities.every((characterAvailability) => {
            let isDisabled = !this.timeSlots.includes(characterAvailability)
            let lowPopularity = !isDisabled && this.sortedAvailabilities.find((availability) => availability.timeSlot === characterAvailability).availableCharacters.length <= this.options.lowPopularityThreshold
    
            return isDisabled || lowPopularity
          })
        })
        let characterWithLowestAvailabilities = charactersForTimeSlot[0]
        let bestCharacterForLegion = characterWithOnlyThatAvailability || characterWithUnpopularOtherAvailabilities || characterWithLowestAvailabilities
        if (bestCharacterForLegion) currentLegion.assign(bestCharacterForLegion)
      }
    })

    // Distribute remaining compatible characters
    this.distribute(this.availableCharacters)

    // Move characters between same time slot legions if some are incomplete
    this.incompleteLegions.forEach((incompleteLegion) => {
      let lenderLegion = this.legions.find((legion) => legion.timeSlot === incompleteLegion.timeSlot && legion !== incompleteLegion && legion.characters.length > minLegionSize)
      if (lenderLegion) {
        let movingCharacters = lenderLegion.characters.slice(0, lenderLegion.characters.length - minLegionSize)
        lenderLegion.moveToLegion(movingCharacters, incompleteLegion)
      }
    })
    
    // Move characters between different time slot legions if some are incomplete
    this.incompleteLegions.forEach((incompleteLegion) => {
      let missingSpots = minLegionSize - incompleteLegion.characters.length
      Array(missingSpots).fill().forEach(() => {
        let lenderLegion = this.legions.find((legion) => legion.characters.length > minLegionSize && legion.characters.some((character) => character.availabilities.includes(incompleteLegion.timeSlot)))
        if (lenderLegion) {
          let movingCharacter = lenderLegion.characters.find((character) => character.availabilities.includes(incompleteLegion.timeSlot))
          if (movingCharacter) lenderLegion.moveToLegion(movingCharacter, incompleteLegion)
        }
      })
    })

    this.unassignedCharacters.sort((character1, character2) => this.options.fillStrategy === 'noAvailability' ? character1.availabilities.length - character2.availabilities.length : character2.availabilities.length - character1.availabilities.length)
    this.incompleteLegions.forEach((incompleteLegion) => {
      if (this.options.groupStrategy === 'maximizeParticipation') {
        // If maximum participation is requested, fill legions with unassigned characters 
        this.fillLegion(incompleteLegion)
      } else if (this.options.groupStrategy === 'minimizeIncompatibilities') {
        // If minimum incompatibility is requested, distribute low popularity incomplete legions characters and disband the legion, fill the high popularity legions
        if (incompleteLegion.characters.length <= this.options.lowPopularityThreshold) {
          this.distribute(incompleteLegion.characters)
          this.disbandLegion(incompleteLegion)
        } else {
          this.fillLegion(incompleteLegion)
        }
      }
    })

    // If there are still incomplete legions, disband them and redistribute characters starting from the least popular
    this.incompleteLegions.forEach(() => {
      this.incompleteLegions.sort((incompleteLegion1, incompleteLegion2) => incompleteLegion1.characters.length - incompleteLegion2.characters.length)
      let incompleteLegion = this.incompleteLegions[0]
      if (incompleteLegion) {
        // Retry to fill it, some characters might have become unassigned
        this.fillLegion(incompleteLegion)
        if (incompleteLegion.incomplete) {
          // If it's still incomplete, disband it and redistribute characters
          this.distribute(incompleteLegion.characters)
          this.disbandLegion(incompleteLegion)
        }
      }
    })

    return this
  }
}
